import { useState } from "react";
import { useEvents, EventWithDetails } from "@/hooks/useEvents";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Calendar, ExternalLink, Image, Video, Loader2, Star } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { localToUtc, utcToLocalParts, formatEventTime, getTimezoneLabel } from "@/lib/eventTimezone";
import { supabase } from "@/integrations/supabase/client";

const AUDIENCE_OPTIONS = [
  { value: "entrepreneur", label: "Entrepreneurs" },
  { value: "mentor", label: "Mentors" },
];

const TIMEZONE_OPTIONS = [
  { value: "America/Jamaica", label: "Jamaica (EST)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
  { value: "UTC", label: "UTC" },
];

interface AttachmentInput {
  type: string;
  url: string;
  file_name?: string;
  title?: string;
  file?: File;
  is_featured?: boolean;
}

export function EventManager() {
  const { events, isLoading, createEvent, updateEvent, deleteEvent } = useEvents();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventWithDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_date: "",
    event_time: "",
    timezone: "America/Jamaica",
    registration_link: "",
    is_active: true,
    show_on_news: false,
    audiences: [] as string[],
    attachments: [] as AttachmentInput[],
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      event_date: "",
      event_time: "",
      timezone: "America/Jamaica",
      registration_link: "",
      is_active: true,
      show_on_news: false,
      audiences: [],
      attachments: [],
    });
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleEdit = (event: EventWithDetails) => {
    setSelectedEvent(event);
    const eventTimezone = event.timezone || "America/Jamaica";
    // Convert UTC timestamp from DB to local date/time parts in event's timezone
    const { date: dateStr, time: timeStr } = utcToLocalParts(event.event_date, eventTimezone);
    setFormData({
      title: event.title,
      description: event.description || "",
      event_date: dateStr,
      event_time: timeStr,
      timezone: eventTimezone,
      registration_link: event.registration_link || "",
      is_active: event.is_active,
      show_on_news: event.show_on_news,
      audiences: event.audiences.map((a) => a.role),
      attachments: event.attachments.map((a) => ({
        type: a.attachment_type,
        url: a.url,
        file_name: a.file_name || undefined,
        title: a.title || undefined,
        is_featured: a.is_featured || false,
      })),
    });
    setShowEditDialog(true);
  };

  const handleDelete = (event: EventWithDetails) => {
    setSelectedEvent(event);
    setShowDeleteDialog(true);
  };

  /**
   * Uploads a file and returns the storage PATH (not full URL)
   */
  const uploadFile = async (file: File, type: "image" | "video"): Promise<string> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `event-attachments/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("attachments")
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Return the path, not the full URL
    return filePath;
  };

  const handleSubmit = async (isEdit: boolean) => {
    if (!formData.title || !formData.event_date) {
      toast.error("Please fill in required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert local date+time in event timezone to UTC for database storage
      const eventDateTime = localToUtc(
        formData.event_date,
        formData.event_time || "00:00",
        formData.timezone
      );

      // Upload any new files
      const processedAttachments = await Promise.all(
        formData.attachments.map(async (att) => {
          if (att.file) {
            const url = await uploadFile(att.file, att.type as "image" | "video");
            return {
              type: att.type,
              url,
              file_name: att.file.name,
              title: att.title,
              is_featured: att.is_featured || false,
            };
          }
          return {
            type: att.type,
            url: att.url,
            file_name: att.file_name,
            title: att.title,
            is_featured: att.is_featured || false,
          };
        })
      );

      if (isEdit && selectedEvent) {
        await updateEvent.mutateAsync({
          id: selectedEvent.id,
          title: formData.title,
          description: formData.description || undefined,
          event_date: eventDateTime,
          timezone: formData.timezone || undefined,
          registration_link: formData.registration_link || undefined,
          is_active: formData.is_active,
          show_on_news: formData.show_on_news,
          audiences: formData.audiences,
          attachments: processedAttachments,
        });
        toast.success("Event updated successfully");
        setShowEditDialog(false);
      } else {
        await createEvent.mutateAsync({
          title: formData.title,
          description: formData.description || undefined,
          event_date: eventDateTime,
          timezone: formData.timezone || undefined,
          registration_link: formData.registration_link || undefined,
          is_active: formData.is_active,
          show_on_news: formData.show_on_news,
          audiences: formData.audiences,
          attachments: processedAttachments,
        });
        toast.success("Event created successfully");
        setShowCreateDialog(false);
      }
      resetForm();
    } catch (error) {
      console.error(error);
      const detail = error instanceof Error && error.message ? `: ${error.message}` : "";
      toast.error(`${isEdit ? "Failed to update event" : "Failed to create event"}${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedEvent) return;

    try {
      await deleteEvent.mutateAsync(selectedEvent.id);
      toast.success("Event deleted successfully");
      setShowDeleteDialog(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete event");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: "image" | "video") => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments: AttachmentInput[] = [];
    for (let i = 0; i < files.length; i++) {
      newAttachments.push({
        type,
        url: URL.createObjectURL(files[i]),
        file: files[i],
        file_name: files[i].name,
      });
    }

    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));
  };

  const handleVideoUrlAdd = () => {
    const url = prompt("Enter video URL (YouTube or Vimeo):");
    if (url) {
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, { type: "video", url, title: "Video" }],
      }));
    }
  };

  const removeAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const setFeaturedImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.map((att, i) => ({
        ...att,
        is_featured: att.type === "image" ? i === index : false,
      })),
    }));
  };

  const toggleAudience = (audience: string) => {
    setFormData((prev) => ({
      ...prev,
      audiences: prev.audiences.includes(audience)
        ? prev.audiences.filter((a) => a !== audience)
        : [...prev.audiences, audience],
    }));
  };

  const formContent = (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Event title"
        />
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Event description"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="event_date">Date *</Label>
          <Input
            id="event_date"
            type="date"
            value={formData.event_date}
            onChange={(e) => setFormData((prev) => ({ ...prev, event_date: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="event_time">Time</Label>
          <Input
            id="event_time"
            type="time"
            value={formData.event_time}
            onChange={(e) => setFormData((prev) => ({ ...prev, event_time: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="timezone">Timezone</Label>
          <Select
            value={formData.timezone}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, timezone: value }))}
          >
            <SelectTrigger id="timezone">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="registration_link">Registration Link</Label>
        <Input
          id="registration_link"
          value={formData.registration_link}
          onChange={(e) => setFormData((prev) => ({ ...prev, registration_link: e.target.value }))}
          placeholder="https://..."
        />
      </div>

      <div>
        <Label>Target Audience</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Leave empty to show to all users
        </p>
        <div className="flex flex-wrap gap-3">
          {AUDIENCE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`audience-${option.value}`}
                checked={formData.audiences.includes(option.value)}
                onCheckedChange={() => toggleAudience(option.value)}
              />
              <Label htmlFor={`audience-${option.value}`} className="font-normal cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Active</Label>
            <p className="text-xs text-muted-foreground">Show this event to users</p>
          </div>
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Show on News Page</Label>
            <p className="text-xs text-muted-foreground">Display on public News page</p>
          </div>
          <Switch
            checked={formData.show_on_news}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, show_on_news: checked }))}
          />
        </div>
      </div>

      <div>
        <Label>Attachments</Label>
        <div className="flex gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => document.getElementById("image-upload")?.click()}
          >
            <Image className="h-4 w-4 mr-1" />
            Add Image
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleVideoUrlAdd}>
            <Video className="h-4 w-4 mr-1" />
            Add Video URL
          </Button>
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e, "image")}
          />
        </div>

        {formData.attachments.length > 0 && (
          <div className="mt-3 space-y-2">
            {formData.attachments.filter(a => a.type === "image").length > 1 && (
              <p className="text-xs text-muted-foreground">
                Click the star to set the featured image (shown at top)
              </p>
            )}
            {formData.attachments.map((att, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  {att.type === "image" ? (
                    <Image className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Video className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm truncate max-w-[200px]">
                    {att.file_name || att.url}
                  </span>
                  {att.is_featured && att.type === "image" && (
                    <Badge variant="secondary" className="text-xs">Featured</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {att.type === "image" && formData.attachments.filter(a => a.type === "image").length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFeaturedImage(index)}
                      className={att.is_featured ? "text-yellow-500" : "text-muted-foreground hover:text-yellow-500"}
                    >
                      <Star className={`h-4 w-4 ${att.is_featured ? "fill-current" : ""}`} />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAttachment(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Events</h3>
          <p className="text-sm text-muted-foreground">
            Manage events and announcements for the platform
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </div>

      {events && events.length > 0 ? (
        <div className="grid gap-4">
          {events.map((event) => (
            <Card key={event.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-base">{event.title}</CardTitle>
                      {!event.is_active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      {event.show_on_news && (
                        <Badge variant="outline">Public</Badge>
                      )}
                    </div>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatEventTime(event.event_date, event.timezone || "America/Jamaica", "PPP 'at' p")}
                        <span className="text-xs">({getTimezoneLabel(event.timezone || "America/Jamaica")})</span>
                      </span>
                      {event.registration_link && (
                        <a
                          href={event.registration_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Register
                        </a>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(event)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(event)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {event.description && (
                  <p className="text-sm text-muted-foreground mb-3">{event.description}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {event.audiences.length > 0 ? (
                    event.audiences.map((aud) => (
                      <Badge key={aud.id} variant="secondary">
                        {aud.role}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="secondary">All Users</Badge>
                  )}
                  {event.attachments.length > 0 && (
                    <Badge variant="outline">
                      {event.attachments.length} attachment{event.attachments.length > 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No events yet</p>
            <Button onClick={handleCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Create your first event
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
            <DialogDescription>Add a new event to the platform</DialogDescription>
          </DialogHeader>
          {formContent}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
            <DialogDescription>Update event details</DialogDescription>
          </DialogHeader>
          {formContent}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit(true)} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedEvent?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
